import React, { useRef, useState } from "react";

type Point = [number, number];
type Rect = [number, number, number, number];

type FieldConfig = {
  key?: string;
  text: string;
  x: number;
  y: number;
  width?: number;
  height?: number;
  size?: number;
  style?: string;
};

type CheckboxConfig = {
  key?: string;
  label?: string;
  checkbox: Rect;
  label_anchor: Point;
};

type CustomLabLineConfig = {
  checkbox: Rect;
  label_anchor: Point;
};

type CalibrationConfig = {
  clearRegions: Rect[];
  fields: FieldConfig[];
  checkboxes: CheckboxConfig[];
  customLabLines: CustomLabLineConfig[];
};

type TemplateCalibration = {
  key: string;
  label: string;
  previewAvailable: boolean;
  previewUrl: string | null;
  previewPath: string;
  templatePath: string;
  config: CalibrationConfig;
};

type Props = {
  authenticityToken: string;
  selectedTemplateKey: string;
  templates: TemplateCalibration[];
  testPdfUrl: string;
  pageWidth: number;
  pageHeight: number;
};

type ItemType = "clear_region" | "field" | "checkbox" | "checkbox_label" | "custom_lab_checkbox" | "custom_lab_label";

type CalibrationItem = {
  id: string;
  type: ItemType;
  index: number;
  name: string;
  x: number;
  y: number;
  width?: number;
  height?: number;
  color: string;
  kindLabel: string;
};

type DragState = {
  itemId: string;
  mode: "move" | "resize";
  pointerStartX: number;
  pointerStartY: number;
  originX: number;
  originY: number;
  originWidth: number;
  originHeight: number;
};

type VisibilityMap = Record<string, boolean>;

const DEFAULT_CLEAR_REGION_WIDTH = 220;
const DEFAULT_CLEAR_REGION_HEIGHT = 60;
const NEW_CLEAR_REGION_OFFSET = 16;

function roundCoordinate(value: number) {
  return Math.max(0, Math.round(value));
}

function prettyName(value: string) {
  return value
    .replaceAll("_", " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function buildItems(config: CalibrationConfig): CalibrationItem[] {
  const fieldItems = config.fields.map((field, index) => ({
    id: `field:${index}`,
    type: "field" as const,
    index,
    name: prettyName(field.key || `Field ${index + 1}`),
    x: field.x,
    y: field.y,
    width: field.width,
    height: field.height,
    color: "emerald",
    kindLabel: "Text"
  }));

  const checkboxItems = config.checkboxes.flatMap((checkbox, index) => [
    {
      id: `checkbox:${index}`,
      type: "checkbox" as const,
      index,
      name: `${prettyName(checkbox.key || `Checkbox ${index + 1}`)} Box`,
      x: checkbox.checkbox[0],
      y: checkbox.checkbox[1],
      width: checkbox.checkbox[2],
      height: checkbox.checkbox[3],
      color: "rose",
      kindLabel: "Checkbox"
    },
    {
      id: `checkbox_label:${index}`,
      type: "checkbox_label" as const,
      index,
      name: `${prettyName(checkbox.key || `Checkbox ${index + 1}`)} Label`,
      x: checkbox.label_anchor[0],
      y: checkbox.label_anchor[1],
      color: "sky",
      kindLabel: "Label"
    }
  ]);

  const customLabItems = config.customLabLines.flatMap((line, index) => [
    {
      id: `custom_lab_checkbox:${index}`,
      type: "custom_lab_checkbox" as const,
      index,
      name: `Custom Lab ${index + 1} Box`,
      x: line.checkbox[0],
      y: line.checkbox[1],
      width: line.checkbox[2],
      height: line.checkbox[3],
      color: "rose",
      kindLabel: "Checkbox"
    },
    {
      id: `custom_lab_label:${index}`,
      type: "custom_lab_label" as const,
      index,
      name: `Custom Lab ${index + 1} Label`,
      x: line.label_anchor[0],
      y: line.label_anchor[1],
      color: "sky",
      kindLabel: "Label"
    }
  ]);

  const clearRegionItems = config.clearRegions.map((region, index) => ({
    id: `clear_region:${index}`,
    type: "clear_region" as const,
    index,
    name: `Clear Region ${index + 1}`,
    x: region[0],
    y: region[1],
    width: region[2],
    height: region[3],
    color: "amber",
    kindLabel: "Clear"
  }));

  return [...fieldItems, ...checkboxItems, ...customLabItems, ...clearRegionItems];
}

function buildVisibilityMap(config: CalibrationConfig): VisibilityMap {
  return Object.fromEntries(buildItems(config).map((item) => [item.id, true]));
}

function chipClasses(color: string) {
  switch (color) {
    case "amber":
      return "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/50 dark:text-amber-100";
    case "rose":
      return "border-rose-200 bg-rose-50 text-rose-900 dark:border-rose-900/60 dark:bg-rose-950/50 dark:text-rose-100";
    case "sky":
      return "border-sky-200 bg-sky-50 text-sky-900 dark:border-sky-900/60 dark:bg-sky-950/50 dark:text-sky-100";
    default:
      return "border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900/60 dark:bg-emerald-950/50 dark:text-emerald-100";
  }
}

function overlayClasses(type: ItemType, active: boolean) {
  const activeShadow = active ? "shadow-[0_0_0_2px_rgba(255,255,255,0.8)]" : "";

  if (type === "clear_region") {
    return `border-2 border-dashed border-amber-500/90 bg-amber-300/15 ${activeShadow}`;
  }

  if (type === "checkbox" || type === "custom_lab_checkbox") {
    return `border-2 border-rose-500 bg-rose-400/10 ${activeShadow}`;
  }

  return activeShadow;
}

function exportConfig(config: CalibrationConfig) {
  const roundPoint = (point: Point): Point => [roundCoordinate(point[0]), roundCoordinate(point[1])];
  const roundRect = (rect: Rect): Rect => [
    roundCoordinate(rect[0]),
    roundCoordinate(rect[1]),
    roundCoordinate(rect[2]),
    roundCoordinate(rect[3])
  ];

  return JSON.stringify(
    {
      clearRegions: config.clearRegions.map(roundRect),
      fields: config.fields.map((field) => ({
        ...field,
        x: roundCoordinate(field.x),
        y: roundCoordinate(field.y),
        ...(field.width !== undefined ? { width: roundCoordinate(field.width) } : {}),
        ...(field.height !== undefined ? { height: roundCoordinate(field.height) } : {})
      })),
      checkboxes: config.checkboxes.map((checkbox) => ({
        ...checkbox,
        checkbox: roundRect(checkbox.checkbox),
        label_anchor: roundPoint(checkbox.label_anchor)
      })),
      customLabLines: config.customLabLines.map((line) => ({
        checkbox: roundRect(line.checkbox),
        label_anchor: roundPoint(line.label_anchor)
      }))
    },
    null,
    2
  );
}

function itemCardClasses(active: boolean, visible: boolean) {
  return [
    "rounded-xl border p-3 transition",
    active
      ? "border-emerald-400 bg-emerald-50/70 dark:border-emerald-500/60 dark:bg-emerald-950/30"
      : "border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-950/50",
    visible ? "" : "opacity-60"
  ].join(" ");
}

function editableFields(item: CalibrationItem): Array<{ key: "x" | "y" | "width" | "height"; label: string }> {
  return [
    { key: "x", label: "X" },
    { key: "y", label: "Y" },
    ...(item.width !== undefined ? [ { key: "width" as const, label: "Width" } ] : []),
    ...(item.height !== undefined ? [ { key: "height" as const, label: "Height" } ] : [])
  ];
}

function preserveVisibility(current: VisibilityMap, config: CalibrationConfig): VisibilityMap {
  return Object.fromEntries(
    buildItems(config).map((item) => [item.id, current[item.id] ?? true])
  );
}

export default function OrderSlipCalibration(props: Props) {
  const initialTemplate = props.templates.find((template) => template.key === props.selectedTemplateKey) || props.templates[0];
  const [selectedTemplateKey, setSelectedTemplateKey] = useState<string>(initialTemplate?.key || "");
  const [config, setConfig] = useState<CalibrationConfig>(initialTemplate?.config || { clearRegions: [], fields: [], checkboxes: [], customLabLines: [] });
  const [visibleItems, setVisibleItems] = useState<VisibilityMap>(() => buildVisibilityMap(config));
  const [selectedId, setSelectedId] = useState<string>(() => buildItems(config)[0]?.id || "");
  const [zoom, setZoom] = useState<number>(0.7);
  const [showGrid, setShowGrid] = useState<boolean>(true);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");
  const boardRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<DragState | null>(null);

  const template = props.templates.find((candidate) => candidate.key === selectedTemplateKey) || initialTemplate;
  const items = buildItems(config);
  const selectedItem = items.find((item) => item.id === selectedId) || items[0];
  const exportJson = exportConfig(config);

  function selectTemplate(nextTemplateKey: string) {
    const nextTemplate = props.templates.find((candidate) => candidate.key === nextTemplateKey);
    if (!nextTemplate) return;

    setSelectedTemplateKey(nextTemplate.key);
    setConfig(nextTemplate.config);
    setVisibleItems(buildVisibilityMap(nextTemplate.config));
    setSelectedId(buildItems(nextTemplate.config)[0]?.id || "");
    setCopyState("idle");
  }

  function updateItem(itemId: string, patch: Partial<CalibrationItem>) {
    const [type, rawIndex] = itemId.split(":");
    const index = Number(rawIndex);
    if (!type || !Number.isInteger(index)) return;

    setConfig((current) => {
      if (type === "field") {
        return {
          ...current,
          fields: current.fields.map((field, fieldIndex) =>
            fieldIndex === index
              ? {
                  ...field,
                  x: roundCoordinate(patch.x ?? field.x),
                  y: roundCoordinate(patch.y ?? field.y),
                  ...(field.width !== undefined ? { width: roundCoordinate(patch.width ?? field.width) } : {}),
                  ...(field.height !== undefined ? { height: roundCoordinate(patch.height ?? field.height) } : {})
                }
              : field
          )
        };
      }

      if (type === "clear_region") {
        return {
          ...current,
          clearRegions: current.clearRegions.map((region, regionIndex) =>
            regionIndex === index
              ? [
                  roundCoordinate(patch.x ?? region[0]),
                  roundCoordinate(patch.y ?? region[1]),
                  roundCoordinate(patch.width ?? region[2]),
                  roundCoordinate(patch.height ?? region[3])
                ]
              : region
          )
        };
      }

      if (type === "checkbox" || type === "custom_lab_checkbox") {
        const collectionKey = type === "checkbox" ? "checkboxes" : "customLabLines";
        return {
          ...current,
          [collectionKey]: current[collectionKey].map((entry, entryIndex) =>
            entryIndex === index
              ? {
                  ...entry,
                  checkbox: [
                    roundCoordinate(patch.x ?? entry.checkbox[0]),
                    roundCoordinate(patch.y ?? entry.checkbox[1]),
                    roundCoordinate(patch.width ?? entry.checkbox[2]),
                    roundCoordinate(patch.height ?? entry.checkbox[3])
                  ] as Rect
                }
              : entry
          )
        };
      }

      if (type === "checkbox_label" || type === "custom_lab_label") {
        const collectionKey = type === "checkbox_label" ? "checkboxes" : "customLabLines";
        return {
          ...current,
          [collectionKey]: current[collectionKey].map((entry, entryIndex) =>
            entryIndex === index
              ? {
                  ...entry,
                  label_anchor: [roundCoordinate(patch.x ?? entry.label_anchor[0]), roundCoordinate(patch.y ?? entry.label_anchor[1])] as Point
                }
              : entry
          )
        };
      }

      return current;
    });
  }

  function boardCoordinates(clientX: number, clientY: number) {
    const board = boardRef.current;
    if (!board) return null;

    const rect = board.getBoundingClientRect();
    return {
      x: (clientX - rect.left) / zoom,
      y: (clientY - rect.top) / zoom
    };
  }

  function beginDrag(event: React.PointerEvent<HTMLDivElement>, item: CalibrationItem, mode: "move" | "resize") {
    const coordinates = boardCoordinates(event.clientX, event.clientY);
    if (!coordinates) return;

    dragRef.current = {
      itemId: item.id,
      mode,
      pointerStartX: coordinates.x,
      pointerStartY: coordinates.y,
      originX: item.x,
      originY: item.y,
      originWidth: item.width ?? 0,
      originHeight: item.height ?? 0
    };

    setSelectedId(item.id);
    event.preventDefault();
    event.stopPropagation();
  }

  function setAllItemsVisible(visible: boolean) {
    setVisibleItems(Object.fromEntries(items.map((item) => [item.id, visible])));
  }

  function setItemVisible(itemId: string, visible: boolean) {
    setVisibleItems((current) => ({ ...current, [itemId]: visible }));
  }

  function buildNewClearRegion(): Rect {
    const previousRegion = config.clearRegions[config.clearRegions.length - 1];
    const width = previousRegion?.[2] || DEFAULT_CLEAR_REGION_WIDTH;
    const height = previousRegion?.[3] || DEFAULT_CLEAR_REGION_HEIGHT;
    const maxX = Math.max(0, props.pageWidth - width);
    const maxY = Math.max(0, props.pageHeight - height);

    if (previousRegion) {
      return [
        roundCoordinate(Math.min(maxX, previousRegion[0] + NEW_CLEAR_REGION_OFFSET)),
        roundCoordinate(Math.min(maxY, previousRegion[1] + NEW_CLEAR_REGION_OFFSET)),
        roundCoordinate(width),
        roundCoordinate(height)
      ];
    }

    return [
      roundCoordinate((props.pageWidth - width) / 2),
      roundCoordinate((props.pageHeight - height) / 2),
      roundCoordinate(width),
      roundCoordinate(height)
    ];
  }

  function addClearRegion() {
    const nextIndex = config.clearRegions.length;
    const nextId = `clear_region:${nextIndex}`;
    const nextConfig = {
      ...config,
      clearRegions: [...config.clearRegions, buildNewClearRegion()]
    };

    setConfig(nextConfig);
    setVisibleItems((current) => ({ ...preserveVisibility(current, nextConfig), [nextId]: true }));
    setSelectedId(nextId);
    setCopyState("idle");
  }

  function removeClearRegion(item: CalibrationItem) {
    if (item.type !== "clear_region") return;

    const nextConfig = {
      ...config,
      clearRegions: config.clearRegions.filter((_region, index) => index !== item.index)
    };
    const nextClearRegionIndex = Math.min(item.index, nextConfig.clearRegions.length - 1);
    const nextSelectedId =
      nextClearRegionIndex >= 0
        ? `clear_region:${nextClearRegionIndex}`
        : buildItems(nextConfig)[0]?.id || "";

    setConfig(nextConfig);
    setVisibleItems((current) => preserveVisibility(current, nextConfig));
    setSelectedId(nextSelectedId);
    setCopyState("idle");
  }

  function openTestPdf() {
    const form = document.createElement("form");
    form.method = "post";
    form.action = props.testPdfUrl;
    form.target = "_blank";
    form.style.display = "none";

    const tokenInput = document.createElement("input");
    tokenInput.type = "hidden";
    tokenInput.name = "authenticity_token";
    tokenInput.value = props.authenticityToken;
    form.appendChild(tokenInput);

    const templateInput = document.createElement("input");
    templateInput.type = "hidden";
    templateInput.name = "template_key";
    templateInput.value = selectedTemplateKey;
    form.appendChild(templateInput);

    const configInput = document.createElement("input");
    configInput.type = "hidden";
    configInput.name = "config_json";
    configInput.value = exportJson;
    form.appendChild(configInput);

    document.body.appendChild(form);
    form.submit();
    form.remove();
  }

  function handlePointerMove(event: PointerEvent) {
    const drag = dragRef.current;
    if (!drag) return;

    const coordinates = boardCoordinates(event.clientX, event.clientY);
    if (!coordinates) return;

    const deltaX = coordinates.x - drag.pointerStartX;
    const deltaY = coordinates.y - drag.pointerStartY;

    if (drag.mode === "resize") {
      updateItem(drag.itemId, {
        width: Math.max(4, drag.originWidth + deltaX),
        height: Math.max(4, drag.originHeight + deltaY)
      });
      return;
    }

    updateItem(drag.itemId, {
      x: drag.originX + deltaX,
      y: drag.originY + deltaY
    });
  }

  function handlePointerUp() {
    dragRef.current = null;
    window.removeEventListener("pointermove", handlePointerMove);
    window.removeEventListener("pointerup", handlePointerUp);
  }

  function startDrag(event: React.PointerEvent<HTMLDivElement>, item: CalibrationItem, mode: "move" | "resize") {
    beginDrag(event, item, mode);
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
  }

  async function copyToClipboard() {
    try {
      await navigator.clipboard.writeText(exportJson);
      setCopyState("copied");
    } catch (_error) {
      setCopyState("failed");
    }
  }

  return (
    <section className="grid gap-4 xl:grid-cols-[minmax(0,1.8fr)_22rem]">
      <section className="app-surface p-4">
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex min-w-64 flex-col gap-1 text-sm font-medium text-neutral-700 dark:text-neutral-200">
            <span>Template</span>
            <select className="app-select" value={selectedTemplateKey} onChange={(event) => selectTemplate(event.target.value)}>
              {props.templates.map((candidate) => (
                <option key={candidate.key} value={candidate.key}>
                  {candidate.label}
                </option>
              ))}
            </select>
          </label>

          <label className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-200">
            <span>Zoom</span>
            <input type="range" min="0.35" max="1.4" step="0.05" value={zoom} onChange={(event) => setZoom(Number(event.target.value))} />
            <span className="w-12 text-right text-xs text-neutral-500 dark:text-neutral-400">{Math.round(zoom * 100)}%</span>
          </label>

          <label className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-200">
            <input type="checkbox" checked={showGrid} onChange={(event) => setShowGrid(event.target.checked)} />
            <span>Grid</span>
          </label>

          <button type="button" className="app-button-secondary" onClick={() => setAllItemsVisible(true)}>
            Show all
          </button>

          <button type="button" className="app-button-secondary" onClick={() => setAllItemsVisible(false)}>
            Hide all
          </button>

          <button type="button" className="app-button-secondary" onClick={addClearRegion}>
            Add clear region
          </button>

          <button type="button" className="app-button-secondary" onClick={() => selectTemplate(selectedTemplateKey)}>
            Reset
          </button>

          <button type="button" className="app-button-secondary" onClick={openTestPdf}>
            Open test PDF
          </button>

          <button type="button" className="app-button-primary" onClick={() => void copyToClipboard()}>
            Copy config JSON
          </button>

          {copyState !== "idle" ? (
            <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400">
              {copyState === "copied" ? "Copied." : "Clipboard copy failed."}
            </span>
          ) : null}
        </div>

        <div className="mt-4 overflow-auto rounded-2xl border border-neutral-200 bg-neutral-100/70 p-3 dark:border-neutral-800 dark:bg-neutral-950/50">
          <div
            ref={boardRef}
            className="relative overflow-hidden rounded-xl border border-neutral-300 bg-white shadow-sm dark:border-neutral-700"
            style={{
              width: props.pageWidth * zoom,
              height: props.pageHeight * zoom,
              backgroundImage: showGrid
                ? "linear-gradient(to right, rgba(15, 23, 42, 0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(15, 23, 42, 0.08) 1px, transparent 1px)"
                : undefined,
              backgroundSize: showGrid ? `${50 * zoom}px ${50 * zoom}px` : undefined
            }}
          >
            {template?.previewAvailable && template.previewUrl ? (
              <img src={template.previewUrl} alt="Order slip preview" className="absolute inset-0 h-full w-full object-fill select-none" draggable={false} />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-neutral-50 text-sm text-neutral-500 dark:bg-neutral-950 dark:text-neutral-400">
                Preview image missing at {template?.previewPath}
              </div>
            )}

            <div className="absolute inset-0">
              {items
                .filter((item) => visibleItems[item.id] ?? true)
                .map((item) => {
                  const isSelected = item.id === selectedItem?.id;

                  if (item.type === "field" || item.type === "checkbox_label" || item.type === "custom_lab_label") {
                    return (
                      <div
                        key={item.id}
                        className="absolute cursor-move select-none"
                        style={{ left: item.x * zoom - 6, top: item.y * zoom - 6 }}
                        onClick={() => setSelectedId(item.id)}
                        onPointerDown={(event) => startDrag(event, item, "move")}
                      >
                        <div className={`h-3 w-3 rounded-full border-2 border-white ${item.color === "sky" ? "bg-sky-500" : "bg-emerald-500"} ${overlayClasses(item.type, isSelected)}`} />
                        <div className={`absolute left-4 top-[-0.2rem] whitespace-nowrap rounded-md border px-2 py-1 text-[11px] font-semibold ${chipClasses(item.color)}`}>
                          {item.name}
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={item.id}
                      className={`absolute cursor-move rounded-sm select-none ${overlayClasses(item.type, isSelected)}`}
                      style={{
                        left: item.x * zoom,
                        top: item.y * zoom,
                        width: (item.width || 0) * zoom,
                        height: (item.height || 0) * zoom
                      }}
                      onClick={() => setSelectedId(item.id)}
                      onPointerDown={(event) => startDrag(event, item, "move")}
                    >
                      <div className={`absolute left-0 top-[-1.65rem] whitespace-nowrap rounded-md border px-2 py-1 text-[11px] font-semibold ${chipClasses(item.color)}`}>
                        {item.name}
                      </div>
                      <div
                        className="absolute bottom-[-0.35rem] right-[-0.35rem] h-3.5 w-3.5 rounded-sm border border-white bg-neutral-950 dark:bg-white"
                        onPointerDown={(event) => startDrag(event, item, "resize")}
                      />
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      </section>

      <aside className="app-surface flex flex-col gap-4 p-4">
        <section>
          <div className="flex items-center justify-between gap-3">
            <p className="app-kicker">Calibration items</p>
            <span className="text-xs text-neutral-500 dark:text-neutral-400">{items.length} items</span>
          </div>

          <div className="mt-2 space-y-3">
            {items.map((item) => {
              const visible = visibleItems[item.id] ?? true;

              return (
                <div key={item.id} className={itemCardClasses(item.id === selectedItem?.id, visible)}>
                  <div className="flex items-start justify-between gap-3">
                    <button type="button" className="min-w-0 text-left" onClick={() => setSelectedId(item.id)}>
                      <div className="flex items-center gap-2">
                        <span className={`rounded-full border px-2 py-1 text-[11px] font-semibold ${chipClasses(item.color)}`}>{item.kindLabel}</span>
                        <span className="text-sm font-semibold text-neutral-950 dark:text-white">{item.name}</span>
                      </div>
                    </button>

                    <div className="flex shrink-0 items-center gap-2">
                      <button type="button" className="app-button-secondary px-3 py-1.5 text-xs" onClick={() => setItemVisible(item.id, !visible)}>
                        {visible ? "Hide" : "Show"}
                      </button>

                      {item.type === "clear_region" ? (
                        <button
                          type="button"
                          className="rounded-md border border-rose-200 bg-white px-3 py-1.5 text-xs font-semibold text-rose-700 transition hover:border-rose-300 hover:bg-rose-50 dark:border-rose-900/60 dark:bg-neutral-950 dark:text-rose-300 dark:hover:bg-rose-950/40"
                          onClick={() => removeClearRegion(item)}
                        >
                          Remove
                        </button>
                      ) : null}
                    </div>
                  </div>

                  <p className="mt-3 text-xs text-neutral-500 dark:text-neutral-400">
                    x {roundCoordinate(item.x)}, y {roundCoordinate(item.y)}
                    {item.width !== undefined && item.height !== undefined ? `, w ${roundCoordinate(item.width)}, h ${roundCoordinate(item.height)}` : ""}
                  </p>

                  {item.id === selectedItem?.id ? (
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      {editableFields(item).map((field) => (
                        <label key={`${item.id}-${field.key}`} className="text-sm">
                          <span className="mb-1 block text-xs font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">{field.label}</span>
                          <input
                            type="number"
                            className="app-input"
                            value={item[field.key] ?? 0}
                            onChange={(event) => updateItem(item.id, { [field.key]: Number(event.target.value) })}
                          />
                        </label>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </section>

        <section>
          <p className="app-kicker">Export</p>
          <textarea className="app-input mt-2 min-h-72 font-mono text-xs leading-5" value={exportJson} readOnly />
        </section>

        <section className="rounded-xl border border-neutral-200 bg-neutral-50/70 p-3 text-xs text-neutral-600 dark:border-neutral-800 dark:bg-neutral-950/50 dark:text-neutral-300">
          <p className="font-semibold text-neutral-950 dark:text-white">Paths</p>
          <p className="mt-2 break-all">{template?.templatePath}</p>
          <p className="mt-2 break-all">{template?.previewPath}</p>
        </section>
      </aside>
    </section>
  );
}
